import React, { useState, useMemo } from 'react'
import Title from '../layouts/Title'
import { hexbyone, hexbytwo, hexbythree, hexbyfour, hexbyfive, hexbysix, woollenone, woollentwo, woollenthree, woollenfour, woollenfive, woollensix, pcoone, pcotwo, pcothree, pcofour, pcofive, pcosix, pcoseven, pcoeight, equiportone, equiporttwo, equiportthree, equiportfour, equiportfive, equiportsix, danceone, dancetwo, dancethree, dancefour, dancefive, dancesix, birminghamone, birminghamtwo, birminghamthree, birminghamfour, birminghamfive, birminghamsix, birminghamseven, birminghameight, birminghamnine, birminghamten, groundworkone, groundworktwo, groundworkthree, groundworkfour, groundworkfive, tieone, tietwo, tiethree, tiefour, tiefive, tiesix, tieseven, shopone, shoptwo, shopthree, shopfour, shopfive, meladraone, meladratwo, meladrathree, meladrafour, meladrafive, meladrasix, meladraseven, meladraeight, bigwifeone, bigwifetwo, bigwifethree, bigwifefour, bigwifefive, bigwifesix, tieeight, tienine, northeastone, northeasttwo, northeastthree, northeastfour, northeastfive, northeastsix, northeastseven, northeasteight, northeastnine, shadeshop1, shadeshop2, shadeshop3, shadeshop4, shadeshop5, shadeshop6, shadeshop7, shadeshop8, silkyAnalytics, silkygem1, silkygem2, silkygem3, silkygem4, silkygem5 } from "../../assets/index";
import ProjectsCard from './ProjectsCard';
import ProjectModal from './ProjectModal';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [visibleProjects, setVisibleProjects] = useState(6); // Initial number of projects to show

  // Detailed project data
  const projectsData = useMemo(() => [
    {
      id: 14,
      title: "Silky Gem — Shopify Plus Performance, UX & Platform Optimization",
      shortDescription:
        "Led Shopify Plus storefront stabilization—Impact theme quality, Core Web Vitals, technical SEO, Recharge subscriptions, and analytics—so performance, subscriptions, and tracking stayed reliable at scale.",
      detailedDescription:
        "As Senior E-Commerce Lead for Silky Gem (Shopify Plus), I owned the technical health of the public storefront and the integrations around it: Impact theme quality, performance and Core Web Vitals, technical SEO, subscription commerce on Recharge, checkout and policy compliance, analytics accuracy, and the third-party app surface (Klaviyo, Yotpo, Gorgias, Bundler, Triple Whale, and related tooling). The work was intentionally incremental and test-heavy so merchandising, subscriptions, and paid channels could keep running through theme changes and peak traffic.\n\n" +
        "**Storefront, Impact Theme & Release Discipline**\n" +
        "• Led stabilization, optimization, and post-launch refinement of the Impact theme—tightening Liquid structure, template consistency, and regression risk across homepage, collection, product, and campaign landing templates.\n" +
        "• Ran structured QA and release validation on core templates before and after deploys, catching layout, script, and subscription edge cases early.\n" +
        "• Built and tuned mobile-first pages and landing experiences with emphasis on fast perceived load, accessible markup, and conversion-friendly layout patterns.\n\n" +
        "**Performance, Lighthouse & Core Web Vitals**\n" +
        "• Conducted Lighthouse-driven reviews (alongside manual profiling) to isolate LCP, CLS, render-blocking scripts, layout shift, and inefficient Liquid or asset patterns on high-traffic routes.\n" +
        "• Improved real-world performance by optimizing above-the-fold assets, image delivery, conditional loading, and frontend rendering behavior on mobile and desktop.\n" +
        "• Audited third-party script impact, removed legacy embeds where safe, and reordered or lazy-loaded non-critical tags to cut bloat while preserving marketing and support functionality.\n\n" +
        "**Image Optimization, Technical SEO & Catalog**\n" +
        "• Optimized 1,500+ product and content images through compression, format choices, and structured alt text—supporting accessibility, page speed, and richer search signals.\n" +
        "• Shipped technical SEO improvements across metadata, internal linking, duplicate image cleanup, indexation hygiene, and collection architecture so high-value pages stayed maintainable as the catalog grew.\n" +
        "• Reworked catalog and collection structure for clearer discoverability, scalable tagging, subscription compatibility, and long-term merchandising flexibility.\n\n" +
        "**Checkout, Cart UX, Upsells & Compliance**\n" +
        "• Improved checkout, upsell, cross-sell, and cart flows to reduce friction on both one-time and subscription paths while keeping policy and pricing clarity visible.\n" +
        "• Designed and implemented a custom Terms & Conditions validation flow on Shopify Plus checkout to strengthen compliance at order placement and reduce operational risk.\n" +
        "• Refined cart and checkout messaging so customers saw consistent guidance on policies, subscriptions, and next steps through conversion.\n\n" +
        "**Subscriptions, Recharge & Lifecycle Integrity**\n" +
        "• Audited subscription enrollment from PDP through cart and checkout into Recharge, fixing mapping gaps so eligible SKUs stayed aligned with recurring billing.\n" +
        "• Reviewed customer portal behavior, recurring billing logic, webhooks, and subscription-to-checkout synchronization to keep renewals and changes predictable for both customers and operations.\n\n" +
        "**Analytics, GA4, GTM, Custom Pixel & Reddit CAPI**\n" +
        "• Configured and validated GA4, Google Tag Manager, Shopify Custom Pixel, and server-side tracking so ecommerce events, parameters, and channel attribution stayed trustworthy.\n" +
        "• Implemented Reddit Pixel with Conversions API and a deduplication approach to improve paid social measurement without double-counting browser and server events.\n\n" +
        "**App Ecosystem, Integrations & Script Hygiene**\n" +
        "• Managed integrations across Recharge, Klaviyo, Bundler, Yotpo, Gorgias, and adjacent Shopify apps—prioritizing reliability and lower script overhead where business rules allowed.\n" +
        "• Investigated Shopify Plus discount and promotion constraints and prototyped a custom Discount Allocator in a development environment to document platform limits and future options.\n\n" +
        "**Operations, Support Channels & Risk Controls**\n" +
        "• Troubleshot Google Merchant Center, Google Ads, analytics ownership, and admin-access issues that were fragmenting measurement or blocking channel updates.\n" +
        "• Improved support-facing experiences—claims flows and a structured Gorgias Help Center—so customers could self-serve and support could resolve issues faster.\n" +
        "• Implemented Shopify Flow–based fraud safeguards (including high-risk order handling and minimum-order protections) to cut abusive or low-quality transactions without blocking legitimate buyers.\n\n" +
        "**Collaboration & Outcomes**\n" +
        "• Partnered with operations, marketing, and support stakeholders to sequence work around revenue-sensitive periods and to validate changes against real merchandising and fulfillment workflows.\n" +
        "• Overall focus: a faster, cleaner, more measurable Shopify Plus storefront with more dependable subscriptions and checkout, fewer tracking blind spots, and clearer paths for customers from discovery through purchase.",
      images: [silkygem1, silkygem2, silkygem3, silkygem4, silkygem5, silkyAnalytics],
      skills: [
        "Shopify Plus Development",
        "Impact Theme Customization & QA",
        "Performance Optimization & Core Web Vitals",
        "Lighthouse & Template Auditing",
        "Image Optimization & Accessibility",
        "Catalog Architecture & Taxonomy",
        "Technical SEO Implementation",
        "Checkout, Cart & Upsell UX",
        "Checkout Flow Validation & Compliance",
        "Subscription Architecture (Recharge)",
        "GA4, GTM & Shopify Custom Pixel",
        "Server-Side Tracking (CAPI)",
        "Third-Party App & Script Auditing",
        "Custom Shopify App Prototyping",
        "Shopify Flow Automation & Fraud Controls",
        "Merchant Center, Ads & Analytics Hygiene",
        "Support UX (Gorgias, Claims Flows)",
        "Platform Governance & Security"
      ],
      technologies: [
        "Shopify Plus",
        "Impact Theme",
        "Liquid",
        "JavaScript",
        "Theme Customization",
        "Lighthouse",
        "Core Web Vitals",
        "WebP",
        "GA4",
        "GTM",
        "Shopify Custom Pixel",
        "Server-Side Tracking",
        "CAPI",
        "Recharge",
        "Klaviyo",
        "Yotpo",
        "Gorgias",
        "Triple Whale",
        "Bundler",
        "Reddit Pixel",
        "Shopify Flow",
        "Google Merchant Center",
        "Google Ads",
        "SEO Tools"
      ],
      githubUrl: "",
      liveUrl: "https://www.silkygem.com/"
    },
    {
      id: 13,
      title: "E-Commerce Platform Optimization & Management System – The Shade Shop",
      shortDescription: "Took ownership of the entire e-commerce technical ecosystem, achieving perfect SEO scores, optimizing Core Web Vitals, and building a comprehensive custom backend management system.",
      detailedDescription: "As the primary engineer for The Shade Shop's e-commerce technical ecosystem, I owned frontend performance, backend integrations, SEO health, and internal tooling to deliver measurable business results.\n\n**SEO Remediation & Optimization**\nLed a comprehensive SEO remediation initiative covering metadata strategy, schema markup, image optimization, crawlability, accessibility compliance, and internal link architecture. Achieved and maintained a perfect SEO score of 100 by continuously monitoring technical health, indexing performance, and search visibility across key landing pages and collections.\n\n**Performance Optimization**\nConducted deep performance audits using Lighthouse, WebPageTest, and Chrome DevTools to identify render-blocking resources, JavaScript bottlenecks, and inefficient Liquid patterns. Improved Core Web Vitals by refactoring theme architecture, optimizing asset delivery, reducing layout shifts, and improving Time to Interactive metrics. Migrated more than 3,500 legacy images into modern WebP formats, implemented responsive image sizing, and standardized alt text for accessibility and search optimization. Introduced lazy loading strategies and conditional rendering logic to minimize unnecessary network requests on high-traffic pages.\n\n**Custom Backend Management System**\nDesigned and launched multiple conversion-focused informational pages (How-To, Contact Us, About Us, Trade Accounts) with consistent UX, SEO alignment, and backend form handling. Replaced third-party form builders with a fully custom internal form management system to reduce SaaS costs and improve data ownership.\n\nArchitected a secure backend system supporting multiple form pipelines, CSV exports, audit trails, and scalable data storage. Designed and implemented role-based authentication and authorization logic supporting executive, administrative, and operational users. Migrated historical trade account submissions from third-party tools into the new internal system while preserving relational data integrity.\n\nBuilt comprehensive admin-facing tools including pagination, bulk actions, filtering, and export workflows to improve internal efficiency and streamline operations.\n\n**Custom Shopify App Development**\nDeveloped a custom Shopify app allowing internal teams to attach operational notes directly to purchase order line items, improving cross-team communication by surfacing contextual order information directly within internal workflows.\n\n**Technical Leadership & Collaboration**\nCollaborated closely with leadership, operations, and marketing teams to align technical solutions with revenue and operational goals. Owned the full lifecycle of feature delivery, from requirements gathering and system design through deployment and post-launch optimization. Identified, debugged, and resolved frontend and backend issues related to theme rendering, app integrations, API data handling, and performance bottlenecks to ensure stable production environments.",
      images: [shadeshop1, shadeshop2, shadeshop3, shadeshop4, shadeshop5, shadeshop6, shadeshop7, shadeshop8],
      skills: [
        "Technical SEO & Metadata Strategy",
        "Schema Markup Implementation",
        "Performance Optimization",
        "Core Web Vitals Improvement",
        "Lighthouse & WebPageTest Auditing",
        "Image Optimization & WebP Migration",
        "Site Architecture Enhancement",
        "Lazy Loading & Conditional Rendering",
        "Full-Stack Backend Development",
        "Custom Form Management Systems",
        "Role-Based Authentication & Authorization",
        "Data Migration & Integrity",
        "Shopify App Development",
        "Admin Panel Development",
        "CSV Export & Data Management",
        "API Integration",
        "E-Commerce Platform Development"
      ],
      technologies: [
        "Shopify",
        "Shopify App Development",
        "Liquid",
        "JavaScript",
        "Node.js",
        "Express.js",
        "MySQL",
        "React",
        "Tailwind CSS",
        "Lighthouse",
        "WebPageTest",
        "Chrome DevTools",
        "WebP",
        "RESTful APIs",
        "GraphQL"
      ],
      githubUrl: "",
      liveUrl: "https://theshadeshop.com"
    },
    {
      id: 1,
      title: "Shopify Migration – Hexby",
      shortDescription: "Migrated Hexby from Wix to Shopify, delivering a scalable, mobile-first eCommerce experience that highlights their bold brand and community.",
      detailedDescription: "Hexby, a fast-growing UK fashion brand, started their eCommerce journey on Wix, but as demand soared, they quickly outgrew the platform. I led their full migration to Shopify, ensuring a smooth transition of products, customers, and order history using custom data export scripts. \n\nWith 93% of traffic coming from mobile, I took a mobile-first approach to UX and UI design. Key improvements included feature-rich product pages, bold visual storytelling, and an optimized SEO setup. The Harlequin Shorts – Hexby’s signature product – was given prominence, while ensuring their wider product range could shine. \n\nThe new Shopify site not only provides Hexby with a scalable platform but also acts as a central hub for their vibrant community, events, and brand storytelling.",
      images: [hexbyfive, hexbytwo, hexbyone, hexbythree, hexbyfour, hexbysix],
      skills: [
        "Shopify Migration",
        "UX & UI Design",
        "SEO Optimization",
        "Custom Data Migration Scripts",
        "Mobile-First Design",
        "E-Commerce Strategy"
      ],
      technologies: [
        "Shopify",
        "Wix",
        "Liquid",
        "React",
        "JavaScript",
        "Custom APIs",
        "Tailwind CSS"
      ],
      githubUrl: "",
      liveUrl: "https://hexby.co.uk"
    },
    
    {
      id: 8,
      title: "Shopify Store Optimization – Training Ties",
      shortDescription: "Enhanced the Training Ties Shopify store with checkout improvements, responsive design updates, and custom modal features to boost conversions.",
      detailedDescription: "Training Ties is an educational product designed to help children learn to tie their shoes, with strong website traffic but a low conversion rate. I worked directly with the founder to modernize and optimize their Shopify store, focusing on conversion-focused improvements and responsive design. \n\nKey contributions included redesigning the cart and checkout experience, updating the footer for clarity and navigation, and ensuring full mobile responsiveness across devices. I also implemented custom popup modals to capture user attention and drive engagement, while A/B testing different approaches to identify the highest-performing layout. \n\nThese updates significantly improved the customer journey, providing a smoother shopping experience and setting the foundation for improved conversion rates from Training Ties’ growing audience.",
      images: [tieone, tietwo, tiethree, tiefour, tiefive, tiesix, tieseven, tieeight, tienine],
      skills: [
        "Shopify Development",
        "Conversion Rate Optimization (CRO)",
        "Responsive Design",
        "Checkout Flow Optimization",
        "Custom Modal Implementation",
        "UI/UX Enhancements"
      ],
      technologies: [
        "Shopify",
        "Liquid",
        "JavaScript",
        "Tailwind CSS",
        "Google Analytics"
      ],
      githubUrl: "https://github.com/ErPing-CMS/Training_Ties_Shopify",
      liveUrl: "https://trainingties.com"
    },
    {
      id: 2,
      title: "Shopify Plus Launch – Aran Woollen Mills (D2C)",
      shortDescription: "Transformed Aran Woollen Mills from a wholesale B2B brand into a premium D2C experience with a Shopify Plus launch and full-funnel marketing strategy.",
      detailedDescription: "Established in 1965, Aran Woollen Mills is Ireland’s largest knitwear brand. Traditionally wholesale/B2B, they wanted to expand into the D2C market with a premium Shopify Plus store and a marketing strategy built for growth. \n\nI led the end-to-end project, starting with brand workshops to refine personas and define the customer journey. The Shopify Plus site was designed to balance storytelling and commerce, featuring product craftsmanship highlights, stitch illustrations, and heritage-focused content woven into the eCommerce experience. \n\nOn the marketing side, I built a full-funnel strategy across Meta Ads and Google Ads, with pre-launch lead generation campaigns driving 10,000+ sign-ups. Post-launch, automated Klaviyo email flows nurtured customers with the right message at the right time. \n\nWithin the first three months, the site generated over 1,300 orders, nearly 5M Meta impressions, 73k+ product views, and a Google Ads ROAS of 4.56, firmly establishing Aran Woollen Mills as a global D2C player.",
      images: [woollenone, woollentwo, woollenthree, woollenfour, woollenfive, woollensix],
      skills: [
        "Shopify Plus Development",
        "UI & UX Design",
        "D2C Strategy",
        "Email Marketing (Klaviyo)",
        "Meta Advertising",
        "Google Ads Strategy",
        "Conversion Rate Optimization (CRO)"
      ],
      technologies: [
        "Shopify Plus",
        "Liquid",
        "React",
        "Klaviyo",
        "Meta Ads Manager",
        "Google Ads",
        "Tailwind CSS"
      ],
      githubUrl: "",
      liveUrl: "https://aranwoollenmills.com"
    },
    {
      id: 7,
      title: "Shopify Enhancements & Product Optimization – Groundwork Coffee",
      shortDescription: "Delivered ongoing Shopify improvements for Groundwork Coffee, enhancing the storefront with custom features, product integrations, and UX optimizations.",
      detailedDescription: "At Groundwork Coffee, I contributed to a series of impactful enhancements on their Shopify storefront, focusing on both product management and user experience. My work went beyond standard product listings, as I implemented custom functionality and design optimizations that streamlined operations and improved customer engagement. \n\nKey contributions included updating and managing café locations across the site, optimizing product detail page (PDP) images to better highlight key products, implementing a ‘Sold Out’ feature for improved inventory transparency, and managing subscription products through Recharge integration. \n\nThese improvements not only simplified backend processes but also elevated the customer experience by creating a cleaner, more intuitive storefront. The result was a more engaging online store that aligned with Groundwork Coffee’s growing brand and loyal customer base.",
      images: [groundworkone, groundworktwo, groundworkthree, groundworkfour, groundworkfive],
      skills: [
        "Shopify Development",
        "Product Listing Optimization",
        "Custom Feature Implementation",
        "Recharge Subscription Integration",
        "UX Enhancement",
        "Storefront Optimization"
      ],
      technologies: [
        "Shopify",
        "Liquid",
        "Recharge",
        "JavaScript",
        "Tailwind CSS"
      ],
      githubUrl: "https://github.com/ErPing-CMS/Ground_work_coffee_liquid",
      liveUrl: "https://www.groundworkcoffee.com"
    },
    {
      id: 3,
      title: "Loyalty Program & Email Marketing – P&Co",
      shortDescription: "Designed and launched a tiered loyalty program with Klaviyo email flows for P&Co, boosting AOV by 13% and engaging 18k+ customers within weeks.",
      detailedDescription: "For over a decade, P&Co has built a brand and community that goes beyond clothing, with over 460k social followers and a strong global presence. To continue scaling, they needed a way to nurture their loyal customer base while improving conversions. \n\nI led the design and development of a fully integrated loyalty program tied directly into their Shopify store. Backed by data analysis, we focused on increasing checkout conversion rates by encouraging repeat purchases from existing customers. The program rewarded members with exclusive discounts, early access to collections, and points-based rewards, structured around a tiered system to drive engagement. \n\nAlongside the loyalty program, I developed Klaviyo-powered automated email flows and custom design assets for email and social campaigns, ensuring strong adoption at launch. The rollout was completed in just two months, delivering immediate impact.\n\nResults included 18,347 customer enrollments, over 5 million points earned, 106 rewards claimed, and a 13% increase in average order value. Conversion rates improved across both the UK and US stores, with overall conversions up 28% year-on-year.",
      images: [pcothree, pcoone, pcotwo, pcofour, pcofive, pcosix, pcoseven, pcoeight],
      skills: [
        "Shopify Development",
        "Loyalty Program Design",
        "Email Marketing Strategy",
        "Klaviyo Automation",
        "CRO Optimization",
        "UI Design"
      ],
      technologies: [
        "Shopify",
        "Klaviyo",
        "Liquid",
        "React",
        "Zapier",
        "Tailwind CSS"
      ],
      githubUrl: "",
      liveUrl: "https://pand.co"
    },
    {
      id: 10,
      title: "Meladra (WordPress) – Premier Interior Design Partner",
      shortDescription: "Designed and developed a high-end WordPress website to establish Meladra as a premier interior design provider, featuring a refined brand identity, case studies, and SEO-driven growth strategy.",
      detailedDescription: "Meladra, a luxury interior design firm based in the Pacific Northwest, sought to launch their new brand and position themselves as a trusted, high-end design partner. Their challenge was to stand out in a competitive market and connect with homeowners seeking a refined, full-service design experience.\n\nI collaborated closely with the Meladra team to create a visually stunning and user-friendly WordPress website that embodied their elevated brand identity. Building upon foundational design work, I expanded the digital brand experience, ensuring a cohesive, modern, and aspirational aesthetic across the site.\n\nKey contributions included developing robust case study pages with photography, testimonials, and detailed project overviews, implementing a fully customizable WordPress block editor for easy content management, and enhancing user experience through clean navigation and responsive design. I also initiated SEO strategies such as region-specific landing pages and performance optimizations, helping Meladra strengthen visibility and attract new audiences.\n\nThe result was a polished website that not only elevated Meladra’s online presence but also laid the foundation for long-term digital growth. With ongoing SEO and strategic support, the platform is helping Meladra expand regionally and establish themselves as a premier interior design partner.",
      images: [meladraone, meladratwo, meladrathree, meladrafour, meladrafive, meladrasix, meladraseven, meladraeight],
      skills: [
        "UI/UX Design",
        "WordPress Development",
        "Brand Identity Integration",
        "Responsive Web Design",
        "SEO Strategy",
        "Content Management"
      ],
      technologies: [
        "Figma",
        "WordPress",
        "PHP",
        "JavaScript",
        "CSS3",
        "HTML5"
      ],
      githubUrl: "",
      liveUrl: "https://meladradesign.com/"
    },
    {
      id: 4,
      title: "Shopify Plus Migration – Equiport",
      shortDescription: "Migrated Equiport from a custom-built platform to Shopify Plus with a bespoke product builder, seamless data migration, and international tax compliance.",
      detailedDescription: "Equiport, a premium equestrian supplies retailer, needed to move from their custom-built platform to Shopify Plus. The challenge lay in handling ultra-complex product configurations, such as the Fleece Show Rug with 17 customization options and thousands of possible combinations—far beyond Shopify’s standard limits. \n\nI developed a bespoke product builder using meta-objects, allowing customers to configure products through color swatches, fonts, and layered options. Each customization was bundled under a single hero product while maintaining accurate add-on pricing and international tax compliance. \n\nThe second challenge was data migration. Equiport’s legacy platform stored customer and order data in a non-standard format. I restructured exports to align with Shopify’s API, ensuring smooth import and preserving CRM and email marketing integrations. \n\nThe outcome was a scalable Shopify Plus store with a streamlined business hub, easier product management, and reduced operational overhead. Equiport now benefits from Shopify’s ease of use while offering customers a personalized, premium shopping experience that mirrors one-on-one consultations.",
      images: [equiportone, equiporttwo, equiportthree, equiportfour, equiportfive, equiportsix],
      skills: [
        "Shopify Plus Migration",
        "Custom Product Builder",
        "Data Migration",
        "UX & UI Design",
        "International Tax Compliance",
        "CRM Integration"
      ],
      technologies: [
        "Shopify Plus",
        "Liquid",
        "React",
        "JavaScript",
        "Shopify API",
        "Tailwind CSS"
      ],
      githubUrl: "",
      liveUrl: "https://equiport.co.uk"
    },
    {
      id: 10,
      title: "Shopify Store Redesign & CRO – Big Wife Guy",
      shortDescription: "Redesigned and optimized the Big Wife Guy Shopify store with modernized layouts, CRO improvements, SEO enhancements, and a refreshed visual identity to boost sales.",
      detailedDescription: "Big Wife Guy is a clothing brand using the Impulse Shopify theme, which provides a strong base for modern apparel stores. The site had potential but required a full refresh to align with current CRO and SEO best practices. \n\nI collaborated closely with the brand to modernize the look and feel, starting with a fresh reimplementation of the Impulse framework for flexibility and performance. Key updates included generating and implementing engaging hero banner images with tailored text overlays, creating a clean and consistent color scheme to strengthen brand identity, and refining the flow of product discovery to checkout. \n\nFrom a technical side, I optimized images for faster loading, compressed and cleaned up code, and applied SEO improvements to boost search rankings. These updates not only improved the site’s overall speed and performance but also enhanced the customer journey, driving higher engagement and conversion rates.\n\nThe result was a revitalized storefront that better represented the brand while providing measurable improvements in user experience and conversion performance.",
      images: [bigwifeone, bigwifetwo, bigwifethree, bigwifefour, bigwifefive, bigwifesix],
      skills: [
        "Shopify Development",
        "Conversion Rate Optimization (CRO)",
        "SEO Optimization",
        "Responsive Design",
        "Theme Customization",
        "UI/UX Enhancements"
      ],
      technologies: [
        "Shopify",
        "Impulse Theme",
        "Liquid",
        "JavaScript",
        "Tailwind CSS",
        "Google Analytics",
        "SEO Tools"
      ],
      githubUrl: "https://github.com/ErPing-CMS/bigwifguy_store",
      liveUrl: "https://bigwifeguy.com"
    },
    {
      id: 5,
      title: "Shopify Plus Website Development – Dancing Leopard",
      shortDescription: "Redesigned and rebuilt Dancing Leopard’s site on Shopify Plus, boosting site speed, enabling internationalization, and preparing for peak sales periods like Black Friday.",
      detailedDescription: "Dancing Leopard needed a new Shopify Plus site that could support ambitious growth, international expansion, and heavy seasonal traffic around Black Friday. Their existing site was dated, restrictive, and lacked the flexibility for modern commerce. \n\nI led the development of a new Shopify Plus storefront with customer-first features such as mega navigation, integrated video content, and SEO-optimized page structures. Multiple homepage and product page variations were designed to blend style with usability, ensuring the site fully represented the brand’s bold aesthetic while staying user-friendly. \n\nInternationalization was a key focus, preparing the business to expand into new markets seamlessly. Advanced analytics were also integrated to provide deeper insights into customer behavior and performance. \n\nThe new site launched successfully ahead of Black Friday with a page speed score of 96 (desktop) and 73 (mobile). It was recognized as ‘Best Website’ at the Drapers Digital Awards, validating its blend of design and performance.",
      images: [danceone, dancetwo, dancethree, dancefour, dancefive, dancesix],
      skills: [
        "Shopify Plus Development",
        "UX & UI Design",
        "Internationalization",
        "SEO Optimization",
        "Customer Journey Mapping",
        "Performance Optimization"
      ],
      technologies: [
        "Shopify Plus",
        "Liquid",
        "JavaScript",
        "React",
        "Tailwind CSS",
        "Google Analytics"
      ],
      githubUrl: "",
      liveUrl: "https://dancingleopard.co.uk"
    },
    {
      id: 6,
      title: "Shopify Website Development & Branding – Birmingham Museums",
      shortDescription: "Redesigned and modernized Birmingham Museums’ online shop with a new brand identity, Shopify eCommerce site, and a full marketing strategy to engage a younger audience.",
      detailedDescription: "Birmingham Museums Trust needed to modernize their online shop to generate revenue during the pandemic and beyond. Their mission was to preserve Birmingham’s cultural heritage while reaching new audiences. \n\nI collaborated through brand workshops to define their foundations, refine their tone of voice, and develop new audience personas, particularly targeting younger customers. This guided the design of a stylish, approachable Shopify site with a friendly voice, modern UX, and features like:\n\n• Art Post – easy ordering of handwritten cards featuring artworks.\n• Custom artwork shop – customers could select style, frame, and size to personalize Birmingham’s collection pieces.\n\nAlongside the website build, I implemented a 1-year marketing and SEO strategy, integrated CRM tools, and developed automated Klaviyo email flows (sign-up, abandoned browse/cart, last-chance offers, and product recommendations). \n\nThe result was a high-performing online store that elevated Birmingham Museums’ brand, improved accessibility to their products, and delivered a seamless, conversion-focused shopping experience.",
      images: [birminghamone, birminghamtwo, birminghamthree, birminghamfour, birminghamfive, birminghamsix, birminghamseven, birminghameight, birminghamnine, birminghamten],
      skills: [
        "Shopify Development",
        "Brand Strategy",
        "UX & UI Design",
        "SEO Optimization",
        "CRM & Email Marketing",
        "Marketing Strategy"
      ],
      technologies: [
        "Shopify",
        "Liquid",
        "Klaviyo",
        "JavaScript",
        "Tailwind CSS",
        "Google Analytics",
        "Facebook Ads Manager"
      ],
      githubUrl: "",
      liveUrl: "https://shop.birminghammuseums.org.uk"
    },
    {
      id: 9,
      title: "Shopcart Online Store – E-commerce Platform",
      shortDescription: "Built a full-featured e-commerce storefront with a modern, responsive UI, Clerk-based authentication, Stripe payments, and a polished shopping experience.",
      detailedDescription: "Shopcart is a full-stack e-commerce demo storefront built with Next.js and Sanity, designed to showcase a production-ready shopping experience. I led the frontend architecture and implemented user-focused features such as a responsive product grid, persistent cart, Clerk authentication, and Stripe-powered checkout flows. \n\nKey contributions included creating a modular component library (product cards, quantity controls, cart UI, responsive navigation), integrating Sanity for content and product data, wiring up Stripe payment sessions for seamless checkout, and optimizing the UI for accessibility and mobile-first responsiveness. Additional features included a custom header/footer, search and category filtering, wishlist functionality, and order management tools, completing the full shopping lifecycle. \n\nThe result was a clean, maintainable codebase and a polished user experience, ready for production use or further customization for client stores.",
      images: [shopone, shoptwo, shopthree, shopfour, shopfive],
      skills: [
        "Next.js / React",
        "Headless CMS Integration (Sanity)",
        "Payment Integration (Stripe)",
        "Authentication (Clerk)",
        "Responsive UI & Accessibility",
        "Component-driven Design"
      ],
      technologies: [
        "Next.js",
        "TypeScript",
        "Sanity",
        "Stripe",
        "Clerk",
        "Tailwind CSS"
      ],
      githubUrl: "https://github.com/ErPing-CMS/eshop_shop_cart",
      liveUrl: ""
    },
    {
      id: 12,
      title: "North Eastern Tree (WordPress) — Website Redesign & Optimization",
      shortDescription: "Modernized the North Eastern Tree Care website with a conversion-focused homepage, improved service pages, streamlined contact/quote flows, and mobile-first responsive updates.",
      detailedDescription: "North Eastern Tree, Shrub & Lawn Care is a family-owned full-service tree, shrub, and lawn care company servicing NY, NJ & CT since 1996. I worked directly with the client to modernize their website and improve lead generation by clarifying services, strengthening CTAs, and making the site faster and more accessible. \n\nKey contributions included: \n\n- Rebuilt the homepage to surface core services (Tree Care, Shrub Care, Lawn Care, Tick & Mosquito Treatment, Deer & Geese Repellents) and highlight trust signals (badges, testimonials, ISA certification). \n- Created dedicated service landing pages with clean, scannable content and service area targeting so visitors can quickly find location-specific offerings. \n- Implemented a persistent “Request Free Consultation” CTA and a click-to-call phone control for mobile to improve inbound calls and quote requests. \n- Redesigned the gallery and testimonials sections to use lazy-loading images and lightbox previews to reduce initial load time while showcasing recent work. \n- Added an optimized contact/quote form with validation, anti-spam measures, and direct email/SMS notifications to the operations team for faster lead follow-up. \n- Performed performance optimizations (image compression, critical CSS, script deferral), accessibility improvements (semantic HTML, ARIA where needed), and basic on-page SEO improvements (structured data for local business, meta tags, sitemap). \n\nThese changes improved clarity of the customer journey — making it faster for homeowners and property managers to find services, request a consultation, or call directly — and established a technical foundation for better SEO and conversion tracking.",
      images: [northeastone, northeasttwo, northeastthree, northeastfour, northeastfive, northeastsix, northeastseven, northeasteight, northeastnine],
      skills: [
        "Website Redesign",
        "WordPress",
        "Conversion Rate Optimization (CRO)",
        "Responsive / Mobile-First Design",
        "Lead Capture & Form UX",
        "Performance Optimization",
        "Accessibility Improvements",
        "Local SEO"
      ],
      technologies: [
        "HTML5",
        "WordPress",
        "CSS3 / Tailwind CSS",
        "JavaScript (vanilla)",
        "PHP (CMS integration)",
        "Google Analytics / GA4",
        "Schema.org (LocalBusiness)",
        "Cloudflare / CDN"
      ],
      githubUrl: "",
      liveUrl: "https://northeasterntreecare.com/"
    }

  ], []);

  // Extract unique categories from technologies
  const categories = useMemo(() => {
    const allTechnologies = projectsData.flatMap(project => project.technologies);
    const uniqueCategories = [...new Set(allTechnologies)].filter(tech => 
      ['Shopify', 'Shopify Plus', 'WordPress', 'Wix', 'WooCommerce', 'Next.js', 'React'].includes(tech)
    );
    return ['All', ...uniqueCategories.sort()];
  }, [projectsData]);

  // Filter projects based on selected category
  const filteredProjects = useMemo(() => {
    if (selectedCategory === 'All') {
      return projectsData;
    }
    return projectsData.filter(project => 
      project.technologies.includes(selectedCategory)
    );
  }, [projectsData, selectedCategory]);

  // Get projects to display based on visibility
  const displayedProjects = useMemo(() => {
    return filteredProjects.slice(0, visibleProjects);
  }, [filteredProjects, visibleProjects]);

  // Check if there are more projects to show
  const hasMoreProjects = visibleProjects < filteredProjects.length;

  // Handle showing more projects
  const handleShowMore = () => {
    setVisibleProjects(prev => prev + 3); // Show 3 more projects each time
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <section
      id="projects"
      className="w-full py-20 border-b-[1px] border-b-black"
    >
      <div className="flex justify-center items-center text-center">
        <Title
          title="VISIT MY PORTFOLIO AND KEEP YOUR FEEDBACK"
          des="My Projects"
        />
      </div>
      
      {/* Category Filter Buttons */}
      <div className="flex flex-wrap justify-center gap-4 py-8">
        {categories.map((category) => (
          <button
            key={category}
            className={`px-6 py-2 rounded-full transition-all duration-300 ${
              selectedCategory === category
                ? 'bg-designColor text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => {
              setSelectedCategory(category);
              setVisibleProjects(6); // Reset to initial count when changing category
            }}
          >
            {category}
          </button>
        ))}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 xl:gap-14 items-stretch">
        {displayedProjects.map((project, index) => (
          <ProjectsCard
            key={`${project.id}-${index}`}
            title={project.title}
            des={project.shortDescription}
            src={project.images[0]}
            githubUrl={project.githubUrl}
            liveUrl={project.liveUrl}
            onClick={() => handleProjectClick(project)}
          />
        ))}
      </div>

      {/* Show More Button */}
      {hasMoreProjects && (
        <div className="flex justify-center mt-10">
          <button
            className="px-8 py-3 rounded-full bg-designColor text-white font-medium hover:bg-opacity-90 transition-all duration-300"
            onClick={handleShowMore}
          >
            Show More
          </button>
        </div>
      )}

      {/* Project Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        project={selectedProject}
      />
    </section>
  );
}

export default Projects